import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import QRCode from "react-qr-code";
import {useAuth} from "../../../contexts/AuthContext";
import CFASFooter from "../../../common/SACC/CareersFairContext/CFASFooter";

// My SACC (/cfas/event/home), laid out to match the CFAS V2 prototype.
// Brand: #002B1B, #004225, #A7B8A5, #C3A56A, #F4EFE5, #63345F. Soleil + Proxima Nova.

const PROFILE_QR_BASE = "https://sacc.samaggisamagom.com/cfas/company/view-qr?userID=";
const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

const EventHome = (props) => {
    const navigate = useNavigate();
    const {getTicket} = useAuth();

    // "loading" | "ready" | "missing"
    const [status, setStatus] = useState("loading");
    const [errorReason, setErrorReason] = useState("");
    const [ticketCode, setTicketCode] = useState("");
    const [ticketVerification, setTicketVerification] = useState("");
    const [name, setName] = useState("");
    const [userID, setUserID] = useState("");
    const [shareOn, setShareOn] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        props.setShowCover(true);
        getTicket().then(async (res) => {
            const data = await res.json();
            props.setShowCover(false);
            if (res.status === 200) {
                setTicketCode(data.data["ticket-id"]);
                setTicketVerification(data.data["ticket-verification"]);
                setName(data.data["name"]);
                setUserID(data.data["user-id"]);
                setStatus("ready");
            } else if (res.status === 404 && (data.data["reason"] === "No Associated Profile Information" || data.data["reason"] === "Profile Information Not Found")) {
                alert("Please enter additional profile information before continuing.");
                window.location = "/cfas/hub/register";
            } else if ((res.status === 401 || data?.data?.["reason"] === "Invalid Token") && !isLocalhost) {
                // Not signed in or session expired: go to sign-in and come back here after.
                // Skipped on localhost, matching Main, so you can still see the page while developing.
                window.location.href = "/cfas/hub/sign-in?nav=/cfas/event/home";
            } else {
                // No ticket yet (not registered for the event) or another error.
                setErrorReason(data?.data?.["reason"] || "");
                setStatus("missing");
            }
        }).catch((error) => {
            console.log(error);
            props.setShowCover(false);
            setErrorReason("We couldn't reach the server. Check your connection and try again.");
            setStatus("missing");
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function copyTicketId() {
        try {
            navigator.clipboard.writeText(ticketCode).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
            });
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <div className="ms">
            <style>{CSS}</style>

            {/* Hero */}
            <section className="ms-hero">
                <div className="ms-in">
                    <h1>My SACC</h1>
                    <p>Your ticket, and the employers you applied to who will be in the room.</p>
                </div>
            </section>

            <main className="ms-main">
                <div className="ms-in ms-cols">

                    {/* ===== Left column ===== */}
                    <div className="ms-stack">

                        {/* Ticket */}
                        <section className="ms-card">
                            <div className="ms-card-head">
                                <div className="ms-grow">
                                    <span className="ms-lbl">Ticket</span>
                                    {status === "loading" && <div className="ms-skel-line w60 tall"/>}
                                    {status === "ready" && <h2>Linked, {ticketCode}</h2>}
                                    {status === "missing" && <h2>Not linked yet</h2>}
                                </div>
                                {status === "ready" && <span className="ms-tag ok">Ready</span>}
                                {status === "missing" && <span className="ms-tag warn">Action needed</span>}
                            </div>

                            {status === "loading" && <p className="ms-muted">Getting your ticket…</p>}
                            {status === "ready" && (
                                <p className="ms-muted">
                                    Show the QR in your event pass at the door. Your CFAS profile and ticket are now one record.
                                </p>
                            )}
                            {status === "missing" && (
                                <>
                                    <p className="ms-muted">
                                        Register for the in-person event to get your ticket.
                                        {errorReason && <span className="ms-small"> ({errorReason})</span>}
                                    </p>
                                    <div>
                                        <button className="ms-btn p" onClick={() => navigate("/cfas/event/register")}>Register for SACC</button>
                                    </div>
                                </>
                            )}
                        </section>

                        {/* Employers to meet */}
                        <h2 className="ms-sec-title">Employers to meet</h2>
                        <section className="ms-card ms-empty">
                            <h3>No employers yet</h3>
                            <p className="ms-muted">
                                Apply to a role and the employers attending SACC appear here with their booth numbers.
                            </p>
                            <button className="ms-btn s" onClick={() => navigate("/cfas/apply/home")}>Find roles</button>
                        </section>
                    </div>

                    {/* ===== Right column ===== */}
                    <aside className="ms-stack">

                        {/* On the day */}
                        <section className="ms-card">
                            <h3>On the day</h3>
                            <ul className="ms-bullets">
                                <li>Bring your ticket QR and a few printed CVs.</li>
                                <li>Check your applications in the morning. Statuses change in the last week.</li>
                                <li>Visit the booths above first, then explore.</li>
                            </ul>
                        </section>

                        {/* Event pass */}
                        <section className="ms-card">
                            <span className="ms-lbl">Event pass</span>
                            {status === "ready" ? (
                                <>
                                    <div className="ms-pass">
                                        <div className="ms-qr">
                                            <QRCode value={ticketCode} size={168} fgColor="#002B1B"/>
                                        </div>
                                        <dl className="ms-dl">
                                            <dt>Name</dt>
                                            <dd>{name}</dd>
                                            <dt>Ticket ID</dt>
                                            <dd className="ms-mono">
                                                {ticketCode}
                                                <button className="ms-link" onClick={copyTicketId}>{copied ? "Copied" : "Copy"}</button>
                                            </dd>
                                            <dt>Verification</dt>
                                            <dd className="ms-mono">{ticketVerification}</dd>
                                        </dl>
                                    </div>
                                    <p className="ms-small ms-muted">
                                        Scanned at the door and at each session. Screenshot it if you might be offline at the venue.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="ms-pass-empty">
                                        {status === "loading" ? "Loading pass…" : "QR pass appears once you register"}
                                    </div>
                                    <p className="ms-small ms-muted">Your entry QR shows here after you register for the event.</p>
                                </>
                            )}
                        </section>

                        {/* Share profile */}
                        {status === "ready" && (
                            <section className="ms-card">
                                <div className="ms-tog">
                                    <div>
                                        <h3>Share your profile</h3>
                                        <small>Employers who scan this see your CFAS profile and CV, and can start an application for you.</small>
                                    </div>
                                    <button
                                        className={"ms-sw" + (shareOn ? " on" : "")}
                                        role="switch"
                                        aria-checked={shareOn}
                                        aria-label="Show profile QR code"
                                        onClick={() => setShareOn(!shareOn)}
                                    />
                                </div>
                                {shareOn ? (
                                    <div className="ms-qr ms-qr-center">
                                        <QRCode value={PROFILE_QR_BASE + userID} size={156} fgColor="#002B1B"/>
                                    </div>
                                ) : (
                                    <div className="ms-banner">Hidden. Keep it off while your ticket is scanned at entry.</div>
                                )}
                            </section>
                        )}
                    </aside>
                </div>
            </main>

            <CFASFooter/>
        </div>
    );
};

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Figtree:wght@400;500;600;700&display=swap");
.ms{
  --green:#004225;--deep:#002B1B;--sage:#A7B8A5;--gold:#C3A56A;--gold-deep:#8E7340;--gold-soft:#F3EAD6;
  --cream:#F4EFE5;--ink:#10251C;--muted:#5B6B62;--line:#E1E6DD;--soft:#E6EEE6;
  --ok:#2E7D4F;--ok-soft:#DDF0E3;--warn:#A8651A;--warn-soft:#F8EAD6;
  --mauve:#A77B9B;--plum:#63345F;--plum-soft:#F3EAF1;
  --sh:0 1px 2px rgba(0,43,27,.05),0 10px 28px rgba(0,43,27,.08);
  --f-display:"soleil","Soleil","Outfit",system-ui,sans-serif;
  --f-body:"proxima-nova","Proxima Nova","Figtree",system-ui,sans-serif;--r:14px;
  background:var(--cream);color:var(--ink);font-family:var(--f-body);font-size:16px;line-height:1.55;min-height:100vh;
  width:100vw;max-width:100vw;overflow-x:hidden;
}
.ms *{box-sizing:border-box}
.ms h1,.ms h2,.ms h3{font-family:var(--f-display);color:var(--deep);margin:0;line-height:1.12}
.ms h2{font-size:clamp(1.35rem,2.4vw,1.75rem);font-weight:700;letter-spacing:-.01em}
.ms h3{font-size:1.1rem;font-weight:700}
.ms p{margin:0}
.ms-muted{color:var(--muted)}.ms-small{font-size:.88rem}
.ms-in{max-width:1120px;margin:0 auto;padding-inline:20px}
.ms-lbl{display:block;font-size:.72rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:4px}
.ms-grow{flex:1;min-width:0}

/* hero */
.ms-hero{background:linear-gradient(160deg,var(--green) 0%,var(--deep) 100%);color:#fff;padding-block:48px;border-top:1px solid rgba(255,255,255,.08)}
.ms-hero h1{color:#fff;font-size:clamp(2rem,4vw,2.75rem);font-weight:700;letter-spacing:-.015em;margin-bottom:10px}
.ms-hero p{color:rgba(255,255,255,.82);font-size:1.1rem;max-width:60ch}
@media (min-width:860px){.ms-hero{padding-block:64px}}

/* layout */
/* Footer always at the bottom: the page is a column and main takes up any spare height */
.ms{display:flex;flex-direction:column}
.ms-main{padding-block:36px 80px;flex:1}
.ms-cols{display:grid;gap:22px}
@media (min-width:860px){.ms-cols{grid-template-columns:1fr 340px;align-items:start;gap:28px}.ms-main{padding-top:40px}}
.ms-stack{display:grid;gap:18px;min-width:0;align-content:start}
.ms-card{background:#fff;border-radius:var(--r);padding:22px 24px;display:grid;gap:12px;min-width:0;box-shadow:var(--sh)}
.ms-card-head{display:flex;align-items:flex-start;gap:14px}
.ms-sec-title{margin-top:18px}

/* tags */
.ms-tag{display:inline-flex;align-items:center;gap:6px;font-size:.78rem;font-weight:700;padding:5px 11px;border-radius:999px;white-space:nowrap}
.ms-tag::before{content:"";width:6px;height:6px;border-radius:50%;background:currentColor}
.ms-tag.ok{background:var(--ok-soft);color:var(--ok)}
.ms-tag.warn{background:var(--warn-soft);color:var(--warn)}

/* empty state */
.ms-empty{text-align:center;justify-items:center;padding:36px 24px;gap:14px}
.ms-empty p{max-width:56ch}

/* bullets */
.ms-bullets{margin:0;padding-left:1.2em;display:grid;gap:6px;color:var(--ink);font-size:.9rem}
.ms-bullets li::marker{color:var(--muted)}

/* event pass */
.ms-pass{display:grid;gap:16px;justify-items:center}
.ms-qr{background:#fff;padding:12px;border-radius:12px;border:1px solid var(--line);line-height:0}
.ms-qr svg{display:block;width:168px;height:168px}
.ms-qr-center{justify-self:center}
.ms-qr-center svg{width:156px;height:156px}
.ms-pass-empty{width:140px;height:140px;border:2px dashed var(--plum);border-radius:10px;background:var(--plum-soft);color:var(--plum);display:grid;place-items:center;text-align:center;padding:12px;font-size:.88rem;line-height:1.4}
.ms-dl{display:grid;grid-template-columns:92px 1fr;gap:8px 12px;margin:0;font-size:.92rem;width:100%}
.ms-dl dt{color:var(--muted);font-weight:600;font-size:.8rem;padding-top:2px}
.ms-dl dd{margin:0;color:var(--deep);font-weight:600;word-break:break-all;display:flex;gap:10px;align-items:baseline;flex-wrap:wrap}
.ms-mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.86rem;letter-spacing:.02em}

/* buttons, links */
.ms-btn{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:var(--f-display);font-weight:700;font-size:.98rem;min-height:46px;padding:10px 20px;border-radius:12px;border:1.5px solid transparent;white-space:nowrap;transition:transform .12s}
.ms-btn:hover{transform:translateY(-1px)}
.ms-btn:focus-visible,.ms-link:focus-visible,.ms-sw:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-btn.p{background:var(--green);color:#fff;box-shadow:0 6px 18px rgba(0,66,37,.25)}
.ms-btn.s{background:#fff;color:var(--deep);border-color:var(--deep)}
.ms-link{all:unset;cursor:pointer;color:var(--green);font-family:var(--f-body);font-weight:600;font-size:.8rem;text-decoration:underline;text-underline-offset:3px}

/* toggle */
.ms-tog{display:flex;justify-content:space-between;align-items:center;gap:14px}
.ms-tog small{font-size:.86rem;color:var(--muted);display:block;margin-top:4px}
.ms-sw{all:unset;cursor:pointer;width:46px;height:27px;border-radius:14px;background:#D6DCD3;position:relative;flex:none}
.ms-sw::after{content:"";position:absolute;top:2px;left:2px;width:23px;height:23px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:left .15s}
.ms-sw.on{background:var(--green)}.ms-sw.on::after{left:21px}
.ms-banner{padding:12px 14px;border-radius:12px;font-size:.88rem;background:var(--cream);color:var(--muted)}

/* loading */
.ms-skel-line{height:14px;border-radius:7px;background:var(--soft);animation:ms-pulse 1.2s ease-in-out infinite;margin-top:6px}
.ms-skel-line.tall{height:28px}
.ms-skel-line.w60{width:60%}
@keyframes ms-pulse{50%{opacity:.5}}
@media (prefers-reduced-motion:reduce){.ms-skel-line{animation:none}.ms-btn{transition:none}}
`;

export default EventHome;